import { useState, type ReactNode } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api, call } from "../api/client";
import type { CatalogItem, CatalogPage } from "../api/types";
import { useCharacter } from "../character";
import { source, text } from "../text";
import { Chips } from "./List";
import { LongText } from "./LongText";
import { Sheet } from "./Sheet";

export interface CatalogFacet {
  /** Filter key the module understands, e.g. a category. */
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface CatalogListProps {
  type: string;
  /** Short hint above the list, e.g. what the pick is for. */
  hint?: ReactNode;
  filter?: Record<string, string | number>;
  /** Filter chips above the list. */
  facets?: CatalogFacet[];
  /** Reason why an item cannot be picked for this character, e.g. because it is already taken. */
  unavailable?: (item: CatalogItem) => string | null | undefined;
  /** Focus on the search when the list appears; off when it appears because of another control that keeps the focus, e.g. a tab. */
  autoFocus?: boolean;
  onPick: (item: CatalogItem) => void;
}

const pageSize = 100;

/** Searchable list of a module catalog, filtered by the character's sources; every entry shows its value and facts. Longer lists load more on request. */
export function CatalogList(props: CatalogListProps) {
  const { character, language } = useCharacter();
  const [query, setQuery] = useState("");
  const [facet, setFacet] = useState<Record<string, string>>({});
  const filter = JSON.stringify({ ...(props.filter ?? {}), ...facet });
  const result = useInfiniteQuery({
    queryKey: ["catalog", character.id, props.type, query, filter],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      call<CatalogPage>(
        api.GET("/api/characters/{id}/catalogs/{type}", {
          params: { path: { id: character.id, type: props.type }, query: { q: query || undefined, filter: filter === "{}" ? undefined : filter, page: pageParam, pageSize } },
        }),
      ),
    getNextPageParam: (last, pages) => (pages.length * pageSize < last.total ? pages.length : undefined),
  });
  const items = result.data?.pages.flatMap((p) => p.items) ?? [];
  const total = result.data?.pages.at(-1)?.total ?? 0;
  return (
    <div className="catalog">
      <div className="stack small-gap">
        {props.hint && <p className="muted small">{props.hint}</p>}
        <input type="search" placeholder="Suchen …" value={query} autoFocus={props.autoFocus ?? true} onChange={(e) => setQuery(e.target.value)} aria-label="Suchen" />
        {props.facets?.map((f) => (
          <Chips
            key={f.key}
            ariaLabel={f.label}
            allLabel="Alle"
            options={f.options}
            value={facet[f.key] ?? null}
            onChange={(value) =>
              setFacet((current) => {
                const next = { ...current };
                if (value === null) delete next[f.key];
                else next[f.key] = value;
                return next;
              })
            }
          />
        ))}
      </div>
      {result.isLoading && <p className="muted">Lade …</p>}
      {result.data && items.length === 0 && <p className="muted">Nichts gefunden.</p>}
      <ul className="catalog-list">
        {items.map((item, index) => {
          const reason = props.unavailable?.(item);
          return (
            // A catalog that joins several kinds may repeat an id; the position keeps the rows apart.
            <li key={`${index}-${item.id}`}>
              <button type="button" className="catalog-item" disabled={!!reason} onClick={() => props.onPick(item)}>
                <span className="catalog-main">
                  <span className="catalog-title">
                    <strong>{text(item.label, language)}</strong>
                    {reason && <span className="badge">{reason}</span>}
                  </span>
                  {item.subtitle && <span className="muted small">{text(item.subtitle, language)}</span>}
                  {item.summary && <span className="catalog-summary">{text(item.summary, language)}</span>}
                  {((item.facts ?? []).length > 0 || item.source) && (
                    <span className="catalog-facts">
                      {(item.facts ?? []).map((f, i) => (
                        <span key={i} className="tag">
                          {text(f, language)}
                        </span>
                      ))}
                      {item.source && <span className="source">{source(item.source)}</span>}
                    </span>
                  )}
                </span>
                {item.value && <span className="catalog-value">{text(item.value, language)}</span>}
              </button>
              <LongText value={item.longText} language={language} />
            </li>
          );
        })}
      </ul>
      {result.data && total > items.length && (
        <div className="catalog-more">
          <span className="muted small">
            {items.length} von {total}
          </span>
          <button type="button" className="button secondary small" disabled={result.isFetchingNextPage} onClick={() => void result.fetchNextPage()}>
            {result.isFetchingNextPage ? "Lade …" : "Mehr laden"}
          </button>
        </div>
      )}
    </div>
  );
}

/** The catalog list in a sheet. */
export function CatalogPicker(props: CatalogListProps & { title: string; before?: ReactNode; onClose: () => void }) {
  const { title, before, onClose, ...list } = props;
  return (
    <Sheet title={title} onClose={onClose} wide>
      {before}
      <CatalogList {...list} />
    </Sheet>
  );
}
